import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Trash2, Eye, ShoppingBag } from 'lucide-react';
import { bagsApi } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import EmptyState from '../../components/common/EmptyState';
import ConfirmModal from '../../components/common/ConfirmModal';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

const AdminBags = () => {
  const [bags, setBags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState({ open: false, bag: null });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchBags();
  }, []);

  const fetchBags = async () => {
    try {
      setLoading(true);

      const response = await bagsApi.getAll();

      const bagsData = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.data)
        ? response.data.data
        : Array.isArray(response.data?.bags)
        ? response.data.bags
        : [];

      setBags(bagsData);
    } catch (err) {
      console.error('Error fetching bags:', err);
      toast.error('Çantaları yükləmək mümkün olmadı');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.bag) return;

    try {
      setDeleting(true);
      await bagsApi.delete(deleteModal.bag._id);
      setBags((prev) => prev.filter((b) => b._id !== deleteModal.bag._id));
      toast.success('Çanta uğurla silindi');
      setDeleteModal({ open: false, bag: null });
    } catch (err) {
      console.error('Error deleting bag:', err);
      toast.error('Çantanı silmək mümkün olmadı');
    } finally {
      setDeleting(false);
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return '';
    if (imagePath.startsWith('http')) return imagePath;
    return `${BACKEND_URL}${imagePath}`;
  };

  if (loading) {
    return <LoadingSpinner text="Çantalar yüklənir..." />;
  }

  return (
    <div data-testid="admin-bags">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-serif text-2xl text-brand-brown">Çantaların idarəsi</h1>
          <p className="text-sm text-brand-muted mt-1">{bags.length} çanta mövcuddur</p>
        </div>

        <Link
          to="/admin/add-bag"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-brand-primary to-[#E08E73] text-white px-6 py-3 font-medium hover:shadow-warm transition-all duration-300"
          data-testid="admin-add-bag-btn"
        >
          <Plus className="w-5 h-5" />
          Yeni çanta əlavə et
        </Link>
      </div>

      {bags.length === 0 ? (
        <div className="bg-white rounded-2xl p-8 shadow-card">
          <EmptyState
            icon={ShoppingBag}
            title="Hələ çanta yoxdur"
            description="Kolleksiyanızı qurmağa başlayın"
          />
          <div className="text-center mt-4">
            <Link
              to="/admin/add-bag"
              className="inline-flex items-center gap-2 text-brand-primary hover:underline"
            >
              <Plus className="w-4 h-4" />
              İlk çantanı əlavə et
            </Link>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="bags-table">
              <thead className="bg-brand-cream/50 border-b border-brand-secondary/30">
                <tr>
                  <th className="text-left px-6 py-4 text-xs font-medium text-brand-muted uppercase tracking-wider">
                    Şəkil
                  </th>
                  <th className="text-left px-6 py-4 text-xs font-medium text-brand-muted uppercase tracking-wider">
                    Ad
                  </th>
                  <th className="text-left px-6 py-4 text-xs font-medium text-brand-muted uppercase tracking-wider">
                    Model
                  </th>
                  <th className="text-left px-6 py-4 text-xs font-medium text-brand-muted uppercase tracking-wider">
                    Kateqoriya
                  </th>
                  <th className="text-left px-6 py-4 text-xs font-medium text-brand-muted uppercase tracking-wider">
                    Qiymət
                  </th>
                  <th className="text-right px-6 py-4 text-xs font-medium text-brand-muted uppercase tracking-wider">
                    Əməliyyatlar
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-brand-secondary/20">
                {bags.map((bag, index) => (
                  <tr
                    key={bag._id || index}
                    className="hover:bg-brand-accent/5 transition-colors"
                    data-testid={`bag-row-${bag._id}`}
                  >
                    <td className="px-6 py-4">
                      <div className="w-16 h-16 rounded-xl bg-brand-muted/10 overflow-hidden">
                        {bag.bagImg ? (
                          <img
                            src={getImageUrl(bag.bagImg)}
                            alt={bag.bagAd}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <ShoppingBag className="w-6 h-6 text-brand-muted" />
                          </div>
                        )}
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <p className="font-medium text-brand-brown">{bag.bagAd || 'Adsız'}</p>
                    </td>

                    <td className="px-6 py-4">
                      <p className="text-sm text-brand-muted">{bag.bagNov || '-'}</p>
                    </td>

                    <td className="px-6 py-4">
                      <span className="text-xs bg-brand-accent/30 text-brand-brown px-3 py-1 rounded-full">
                        {bag.bagCategory || 'Kateqoriyasız'}
                      </span>
                    </td>

                    <td className="px-6 py-4">
                      <p className="font-serif text-brand-primary">
                        {Number(bag.bagPrice || 0).toFixed(2)} ₼
                      </p>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/bags/${bag._id}`}
                          target="_blank"
                          className="p-2 rounded-xl text-brand-muted hover:text-brand-primary hover:bg-brand-accent/20 transition-colors"
                          title="Bax"
                          data-testid={`view-bag-${bag._id}`}
                        >
                          <Eye className="w-5 h-5" />
                        </Link>

                        <button
                          onClick={() => setDeleteModal({ open: true, bag })}
                          className="p-2 rounded-xl text-brand-muted hover:text-red-500 hover:bg-red-50 transition-colors"
                          title="Sil"
                          data-testid={`delete-bag-${bag._id}`}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmModal
        open={deleteModal.open}
        onOpenChange={(open) =>
          setDeleteModal({ open, bag: open ? deleteModal.bag : null })
        }
        title="Çantanı sil"
        description={`"${deleteModal.bag?.bagAd}" çantasını silmək istədiyinizə əminsiniz? Bu əməliyyat geri qaytarıla bilməz.`}
        onConfirm={handleDelete}
        isLoading={deleting}
      />
    </div>
  );
};

export default AdminBags;